import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import {
    retryAgentNoteClassification,
    useAdminAgentNote,
    useAdminAgentNotes,
} from '@apps/web/features/admin_agent_notes/api/agent_notes_queries';
import { type AgentNotesSearch } from '@apps/web/features/admin_agent_notes/model/search_params';
import { useAgentNotesQueryParams } from '@apps/web/features/admin_agent_notes/model/use_agent_notes_query_params';
import { toast } from '@apps/web/lib/app_notifications';

const route = getRouteApi('/_app/admin/agent-notes/$note-id');

// Matches the inline feed panel's live refresh so a retried note visibly
// advances to its terminal state without a manual reload.
const LIVE_REFRESH_INTERVAL_MS = 30_000;

/**
 * Controller for the standalone note page: the note itself plus the
 * surrounding filtered list, so filters and prev/next work without going
 * back to the feed.
 */
export function useAgentNoteDetail() {
    const { 'note-id': noteId } = route.useParams();
    const search = route.useSearch();
    const navigate = route.useNavigate();
    const [isRetrying, setIsRetrying] = useState(false);

    const noteQuery = useAdminAgentNote(noteId, {
        refreshIntervalMs: LIVE_REFRESH_INTERVAL_MS,
    });
    const queryParams = useAgentNotesQueryParams(search);
    const notesQuery = useAdminAgentNotes(queryParams, {
        keepPreviousData: true,
    });
    const { mutate: refreshNote } = noteQuery;
    const { mutate: refreshList } = notesQuery;

    const patchSearch = useCallback(
        (patch: Partial<AgentNotesSearch>) => {
            void navigate({
                search: (prev) => ({ ...prev, ...patch }),
                replace: true,
                resetScroll: false,
            });
        },
        [navigate],
    );

    const noteData = notesQuery.data;
    const notes = useMemo(() => noteData?.agent_notes ?? [], [noteData]);
    const noteIndex = notes.findIndex((note) => note.id === noteId);

    const navigateNote = useCallback(
        (direction: 'previous' | 'next') => {
            if (noteIndex < 0) {
                return;
            }
            const sibling =
                notes[direction === 'previous' ? noteIndex - 1 : noteIndex + 1];
            if (sibling) {
                void navigate({
                    to: '/admin/agent-notes/$note-id',
                    params: { 'note-id': sibling.id },
                    search: (prev) => prev,
                    resetScroll: false,
                });
            }
        },
        [navigate, noteIndex, notes],
    );

    // Returns whether the retry was accepted, so the editor keeps the
    // admin's text on failure.
    const retryClassification = useCallback(
        async (targetNoteId: string, content?: string): Promise<boolean> => {
            setIsRetrying(true);
            try {
                const result = await retryAgentNoteClassification(
                    targetNoteId,
                    content,
                );
                if (result.published) {
                    toast.success(
                        'Note reset to pending and queued for classification',
                        { persistNotification: false },
                    );
                } else {
                    toast.warning(
                        'Note reset to pending, but the queue publish failed. The pending sweep will pick it up.',
                        { persistNotification: false },
                    );
                }
                await Promise.all([refreshNote(), refreshList()]);
                return true;
            } catch {
                toast.error('Retry failed. The note was not changed.', {
                    persistNotification: false,
                });
                return false;
            } finally {
                setIsRetrying(false);
            }
        },
        [refreshList, refreshNote],
    );

    return {
        search,
        noteId,
        note: noteQuery.data ?? null,
        isLoading: noteQuery.isLoading,
        hasError:
            noteQuery.error !== undefined ||
            (noteQuery.hasRequestFailed && noteQuery.data === undefined),
        statusCounts: notesQuery.data?.status_counts ?? [],
        // Prev/next step through the filtered list; disabled when the note
        // fell outside the current filters or page.
        hasPreviousNote: noteIndex > 0,
        hasNextNote: noteIndex >= 0 && noteIndex < notes.length - 1,
        isRetrying,
        patchSearch,
        navigateNote,
        retryClassification,
    };
}
